import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import * as React from 'react';

import { ContextStore } from '../store/contextStore';

/**
 * Licensee dropdown for create/edit forms in franchise mode.
 * Returns null when the tour operator is not a franchise.
 *
 * Props:
 *   value        — selected licensee ID (number) or object {id, name} or null
 *   onChange(id)  — called with the numeric licensee ID (or null when "None" selected)
 *   error        — boolean, show error state
 *   helperText   — error helper text
 *   disabled     — disable the field
 *   allowNone    — if true, adds a "None (parent operator)" option and removes required
 *   allowParent  — if true, adds the parent operator itself as a selectable option
 *                  (real TO id, unlike allowNone's null). Gate on franchise.manager.
 *                  The parent option is also shown, regardless of this prop, when the
 *                  record already belongs to the parent — otherwise the field is blank.
 */
const LicenseeFormField = ({ value, onChange, error, helperText, disabled, allowNone, allowParent }) => {
    const { isFranchise, licensees, userLicenseeIds, tourOperator } = React.useContext(ContextStore);

    if (!isFranchise) {
        return null;
    }

    // Normalize: accept both {id, name} objects (from API) and plain IDs
    const normalizedValue = value != null && typeof value === 'object' ? value.id : value;

    const visibleLicensees = licensees.filter(
        (l) => userLicenseeIds.length === 0 || userLicenseeIds.includes(l.id),
    );

    // Parent-owned records (tour_operator = the franchise parent) have no matching
    // licensee option, so the Select would render blank. Always offer the parent when
    // it is the current value, even without allowParent — picking it is a no-op.
    const showParent = !!tourOperator && (allowParent || normalizedValue === tourOperator.id);

    return (
        <FormControl variant="filled" size="small" fullWidth required={!allowNone} error={error}>
            <InputLabel htmlFor="form-licensee-select">Licensee</InputLabel>
            <Select
                value={normalizedValue ?? ''}
                onChange={(e) => onChange(e.target.value || null)}
                disabled={disabled || (!allowNone && !allowParent && visibleLicensees.length <= 1)}
                displayEmpty={!!allowNone}
                inputProps={{ id: 'form-licensee-select' }}
            >
                {allowNone && <MenuItem value="">None (parent operator)</MenuItem>}
                {showParent && (
                    <MenuItem value={tourOperator.id}>{tourOperator.name} (parent operator)</MenuItem>
                )}
                {visibleLicensees.map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                        {l.name}
                    </MenuItem>
                ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};

export default LicenseeFormField;
